import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function FeeSharingSettings() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    transactionType: "",
    companySharePercent: 80,
    agentSharePercent: 20,
    description: "",
    isActive: true
  });

  // Preview calculation
  const [previewFee, setPreviewFee] = useState(100);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user.isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchConfigs();
  }, [navigate]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/FeeSharing", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setConfigs(data);
    } catch (err) {
      setError("Failed to load fee sharing configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");

    if (form.companySharePercent + form.agentSharePercent !== 100) {
      setError("Company and Agent shares must total 100%");
      return;
    }

    try {
      const response = await fetch("/api/FeeSharing", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to save");
      }

      setSuccess("Fee sharing configuration saved!");
      setShowModal(false);
      resetForm();
      fetchConfigs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this configuration?")) return;
    
    try {
      await fetch(`/api/FeeSharing/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Configuration deleted");
      fetchConfigs();
    } catch (err) {
      setError("Failed to delete");
    }
  };

  const handleInitialize = async () => {
    try {
      const response = await fetch("/api/FeeSharing/initialize", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSuccess(data.message);
      fetchConfigs();
    } catch (err) {
      setError("Failed to initialize");
    }
  };

  const openEditModal = (config) => {
    setForm({
      transactionType: config.transactionType,
      companySharePercent: config.companySharePercent,
      agentSharePercent: config.agentSharePercent,
      description: config.description || "",
      isActive: config.isActive
    });
    setEditMode(true);
    setShowModal(true);
  };

  const openAddModal = () => {
    resetForm();
    setEditMode(false);
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      transactionType: "",
      companySharePercent: 80,
      agentSharePercent: 20,
      description: "",
      isActive: true
    });
  };

  const handleCompanyChange = (value) => {
    const company = Math.max(0, Math.min(100, parseFloat(value) || 0));
    setForm({
      ...form,
      companySharePercent: company,
      agentSharePercent: 100 - company
    });
  };

  const handleAgentChange = (value) => {
    const agent = Math.max(0, Math.min(100, parseFloat(value) || 0));
    setForm({
      ...form,
      agentSharePercent: agent,
      companySharePercent: 100 - agent
    });
  };

  const fmt = (n) => `₦${Number(n).toLocaleString()}`;

  // Calculate preview
  const companyPreview = (previewFee * form.companySharePercent / 100).toFixed(2);
  const agentPreview = (previewFee * form.agentSharePercent / 100).toFixed(2);

  if (loading) return <div style={{textAlign:"center",marginTop:"100px"}}>Loading...</div>;

  return (
    <div style={{maxWidth:"1000px",margin:"0 auto",padding:"20px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px",flexWrap:"wrap",gap:"10px"}}>
        <h1>💰 Fee Sharing Settings</h1>
        <div style={{display:"flex",gap:"10px"}}>
          <button onClick={() => navigate("/admin")} style={styles.backButton}>← Back</button>
          <button onClick={handleInitialize} style={styles.initButton}>🔧 Initialize Defaults</button>
          <button onClick={openAddModal} style={styles.addButton}>➕ Add Config</button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Info Card */}
      <div style={styles.infoCard}>
        <h3>📊 How Fee Sharing Works</h3>
        <p>When an agent processes a deposit or withdrawal, the transaction fee is split between:</p>
        <ul>
          <li><strong>Company Share</strong> → Credited to <em>Fee Revenue</em> ledger account</li>
          <li><strong>Agent Share</strong> → Credited to agent's wallet as commission</li>
        </ul>
      </div>

      {/* Configurations Table */}
      <div style={styles.card}>
        <h2>Current Configurations</h2>
        {configs.length === 0 ? (
          <div style={styles.noData}>
            <p>No fee sharing configurations found.</p>
            <button onClick={handleInitialize} style={styles.initButton}>Initialize Default Configs</button>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Transaction Type</th>
                <th style={styles.th}>Company Share</th>
                <th style={styles.th}>Agent Share</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <tr key={config.id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <strong>{config.transactionType}</strong>
                    {config.description && <div style={styles.description}>{config.description}</div>}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.companyBadge}>{config.companySharePercent}%</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.agentBadge}>{config.agentSharePercent}%</span>
                  </td>
                  <td style={styles.td}>
                    <span style={config.isActive ? styles.activeBadge : styles.inactiveBadge}>
                      {config.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => openEditModal(config)} style={styles.editButton}>✏️ Edit</button>
                    <button onClick={() => handleDelete(config.id)} style={styles.deleteButton}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{editMode ? "Edit" : "Add"} Fee Sharing Config</h2>
            <form onSubmit={handleSubmit}>
              <div style={styles.inputGroup}>
                <label>Transaction Type</label>
                <select
                  value={form.transactionType}
                  onChange={(e) => setForm({...form, transactionType: e.target.value})}
                  required
                  disabled={editMode}
                  style={styles.input}
                >
                  <option value="">Select type...</option>
                  <option value="AgentDeposit">Agent Deposit</option>
                  <option value="AgentWithdrawal">Agent Withdrawal</option>
                  <option value="Transfer">Transfer</option>
                  <option value="BillPayment">Bill Payment</option>
                </select>
              </div>

              <div style={styles.sliderSection}>
                <h4>Fee Split Ratio</h4>
                <div style={styles.sliderContainer}>
                  <div style={styles.sliderLabels}>
                    <span>Company: <strong style={{color:"#007bff"}}>{form.companySharePercent}%</strong></span>
                    <span>Agent: <strong style={{color:"#28a745"}}>{form.agentSharePercent}%</strong></span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={form.companySharePercent}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    style={styles.slider}
                  />
                  <div style={styles.sliderBar}>
                    <div style={{...styles.companyBar, width: `${form.companySharePercent}%`}}></div>
                    <div style={{...styles.agentBar, width: `${form.agentSharePercent}%`}}></div>
                  </div>
                </div>

                {/* Manual input */}
                <div style={styles.manualInputs}>
                  <div>
                    <label>Company %</label>
                    <input
                      type="number"
                      value={form.companySharePercent}
                      onChange={(e) => handleCompanyChange(e.target.value)}
                      min="0"
                      max="100"
                      style={styles.smallInput}
                    />
                  </div>
                  <div>
                    <label>Agent %</label>
                    <input
                      type="number"
                      value={form.agentSharePercent}
                      onChange={(e) => handleAgentChange(e.target.value)}
                      min="0"
                      max="100"
                      style={styles.smallInput}
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div style={styles.preview}>
                <h4>Preview (for {fmt(previewFee)} fee)</h4>
                <div style={styles.previewRow}>
                  <span>Company gets:</span>
                  <strong style={{color:"#007bff"}}>{fmt(companyPreview)}</strong>
                </div>
                <div style={styles.previewRow}>
                  <span>Agent gets:</span>
                  <strong style={{color:"#28a745"}}>{fmt(agentPreview)}</strong>
                </div>
                <div style={{marginTop:"10px"}}>
                  <label style={{fontSize:"12px"}}>Test with different fee:</label>
                  <input
                    type="number"
                    value={previewFee}
                    onChange={(e) => setPreviewFee(parseFloat(e.target.value) || 0)}
                    style={{...styles.smallInput, width:"100px", marginLeft:"10px"}}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label>Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  style={{...styles.input, minHeight:"60px"}}
                  placeholder="e.g., Standard 80/20 split for agent deposits"
                />
              </div>

              <div style={styles.inputGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({...form, isActive: e.target.checked})}
                  /> Active
                </label>
              </div>

              <div style={styles.modalButtons}>
                <button type="submit" style={styles.saveButton}>💾 Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  backButton: { padding:"10px 20px", backgroundColor:"#6c757d", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:"600" },
  initButton: { padding:"10px 20px", backgroundColor:"#17a2b8", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:"600" },
  addButton: { padding:"10px 20px", backgroundColor:"#28a745", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:"600" },
  error: { backgroundColor:"#fee", color:"#c33", padding:"15px", borderRadius:"6px", marginBottom:"20px", border:"1px solid #fcc" },
  success: { backgroundColor:"#d4edda", color:"#155724", padding:"15px", borderRadius:"6px", marginBottom:"20px", border:"1px solid #c3e6cb" },
  infoCard: { backgroundColor:"#e7f3ff", padding:"20px", borderRadius:"8px", marginBottom:"20px", border:"1px solid #b6d4fe" },
  card: { backgroundColor:"white", padding:"25px", borderRadius:"8px", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" },
  noData: { textAlign:"center", padding:"40px", color:"#666" },
  table: { width:"100%", borderCollapse:"collapse" },
  tableHeader: { backgroundColor:"#f8f9fa" },
  th: { padding:"12px", textAlign:"left", borderBottom:"2px solid #dee2e6", fontWeight:"600" },
  tableRow: { borderBottom:"1px solid #eee" },
  td: { padding:"12px", verticalAlign:"top" },
  description: { fontSize:"12px", color:"#666", marginTop:"4px" },
  companyBadge: { display:"inline-block", padding:"4px 12px", backgroundColor:"#007bff", color:"white", borderRadius:"12px", fontWeight:"600" },
  agentBadge: { display:"inline-block", padding:"4px 12px", backgroundColor:"#28a745", color:"white", borderRadius:"12px", fontWeight:"600" },
  activeBadge: { display:"inline-block", padding:"4px 12px", backgroundColor:"#28a745", color:"white", borderRadius:"12px", fontSize:"12px" },
  inactiveBadge: { display:"inline-block", padding:"4px 12px", backgroundColor:"#dc3545", color:"white", borderRadius:"12px", fontSize:"12px" },
  editButton: { padding:"6px 12px", backgroundColor:"#007bff", color:"white", border:"none", borderRadius:"4px", cursor:"pointer", marginRight:"5px", fontSize:"13px" },
  deleteButton: { padding:"6px 12px", backgroundColor:"#dc3545", color:"white", border:"none", borderRadius:"4px", cursor:"pointer", fontSize:"13px" },
  modal: { position:"fixed", top:0, left:0, width:"100%", height:"100%", backgroundColor:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 },
  modalContent: { backgroundColor:"white", padding:"30px", borderRadius:"8px", width:"90%", maxWidth:"500px", maxHeight:"90vh", overflow:"auto" },
  inputGroup: { marginBottom:"20px" },
  input: { width:"100%", padding:"10px", fontSize:"14px", border:"1px solid #ddd", borderRadius:"4px", boxSizing:"border-box", marginTop:"5px" },
  sliderSection: { backgroundColor:"#f8f9fa", padding:"20px", borderRadius:"8px", marginBottom:"20px" },
  sliderContainer: { marginTop:"10px" },
  sliderLabels: { display:"flex", justifyContent:"space-between", marginBottom:"10px" },
  slider: { width:"100%", cursor:"pointer" },
  sliderBar: { display:"flex", height:"20px", borderRadius:"10px", overflow:"hidden", marginTop:"10px" },
  companyBar: { backgroundColor:"#007bff", transition:"width 0.2s" },
  agentBar: { backgroundColor:"#28a745", transition:"width 0.2s" },
  manualInputs: { display:"flex", gap:"20px", marginTop:"15px" },
  smallInput: { width:"80px", padding:"8px", border:"1px solid #ddd", borderRadius:"4px", marginTop:"5px" },
  preview: { backgroundColor:"#fff3cd", padding:"15px", borderRadius:"8px", marginBottom:"20px", border:"1px solid #ffc107" },
  previewRow: { display:"flex", justifyContent:"space-between", padding:"5px 0" },
  modalButtons: { display:"flex", gap:"10px", justifyContent:"flex-end" },
  saveButton: { padding:"12px 30px", backgroundColor:"#28a745", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontWeight:"600" },
  cancelButton: { padding:"12px 30px", backgroundColor:"white", color:"#333", border:"1px solid #ddd", borderRadius:"6px", cursor:"pointer" }
};

